// Microphone pitch detector (browser-only, no external service).
// Uses getUserMedia + an AnalyserNode and a bounded autocorrelation to estimate
// the fundamental frequency of a hummed/sung note. Throttled to ~35ms.

function detectPitch(buf, sampleRate) {
  const SIZE = buf.length;
  let rms = 0;
  for (let i = 0; i < SIZE; i++) rms += buf[i] * buf[i];
  rms = Math.sqrt(rms / SIZE);
  if (rms < 0.008) return -1; // too quiet -> no pitch

  // YIN pitch detection (cumulative mean normalized difference) — octave-robust.
  const W = Math.floor(SIZE / 2);
  const maxLag = Math.min(W - 1, Math.floor(sampleRate / 70)); // down to 70 Hz
  const minLag = Math.floor(sampleRate / 1000); // up to 1000 Hz

  const d = new Float32Array(maxLag + 1);
  for (let tau = 1; tau <= maxLag; tau++) {
    let sum = 0;
    for (let i = 0; i < W; i++) {
      const diff = buf[i] - buf[i + tau];
      sum += diff * diff;
    }
    d[tau] = sum;
  }

  const cm = new Float32Array(maxLag + 1);
  cm[0] = 1;
  let running = 0;
  for (let tau = 1; tau <= maxLag; tau++) {
    running += d[tau];
    cm[tau] = (d[tau] * tau) / running;
  }

  const thresh = 0.15;
  let tau = -1;
  for (let t = minLag; t <= maxLag; t++) {
    if (cm[t] < thresh) {
      while (t + 1 <= maxLag && cm[t + 1] < cm[t]) t++;
      tau = t;
      break;
    }
  }
  if (tau === -1) {
    let mn = Infinity;
    for (let t = minLag; t <= maxLag; t++) if (cm[t] < mn) { mn = cm[t]; tau = t; }
    if (mn > 0.3) return -1; // not periodic enough
  }

  // parabolic interpolation for sub-sample accuracy
  const x0 = tau > 1 ? cm[tau - 1] : cm[tau];
  const x2 = tau + 1 <= maxLag ? cm[tau + 1] : cm[tau];
  const a = x0 + x2 - 2 * cm[tau];
  const b = (x2 - x0) / 2;
  let better = tau;
  if (a) better = tau - b / (2 * a);

  return sampleRate / better;
}

export class PitchEngine {
  constructor() {
    this.ctx = null;
    this.analyser = null;
    this.stream = null;
    this.buf = null;
    this.running = false;
    this.onPitch = null;
    this.raf = null;
    this.lastFire = 0;
  }

  async start(onPitch) {
    this.onPitch = onPitch;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!this.ctx) this.ctx = new Ctx();
    if (this.ctx.state === "suspended") await this.ctx.resume();

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    const src = this.ctx.createMediaStreamSource(this.stream);
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    this.buf = new Float32Array(this.analyser.fftSize);
    src.connect(this.analyser);

    this.running = true;
    this.lastFire = 0;
    this._loop();
  }

  _loop = () => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this._loop);
    const now = performance.now();
    if (now - this.lastFire < 35) return; // throttle
    this.lastFire = now;
    this.analyser.getFloatTimeDomainData(this.buf);
    const freq = detectPitch(this.buf, this.ctx.sampleRate);
    if (this.onPitch) this.onPitch(freq);
  };

  stop() {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.analyser = null;
    this.onPitch = null;
  }
}

const NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export function freqToNote(f) {
  const midi = 69 + 12 * Math.log2(f / 440);
  const r = Math.round(midi);
  const pc = ((r % 12) + 12) % 12;
  return { pc, name: NAMES[pc], cents: Math.round((midi - r) * 100), octave: Math.floor(r / 12) - 1 };
}

// natural-note letter -> pitch class (octave independent)
export const NOTE_TO_PC = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
